import type React from 'react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useHover,
    useListNavigation,
    useTypeahead,
    FloatingPortal,
    FloatingList,
    useListItem,
    useMergeRefs,
    offset,
    flip,
    shift,
    autoUpdate,
    safePolygon,
} from '@floating-ui/react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import Button from '../../form/Button';
import { schema } from '../../../schema';
import { SwarmIconClasses, SwarmType, SwarmTypeTranslations } from '../../../enums';
import {
    PARTIAL_CONFIG_TYPE_LABELS,
    createPartialConfigCreateUrl,
    isPartialConfigCategory,
    isSwarmType,
} from './utils';
import classNames from '../../../utils/classNames';

type MenuLink = {
    label: string;
    icon: ReactNode;
    to: string;
};

type MenuGroup = {
    label: string;
    icon: ReactNode;
    children: MenuLink[];
};

type MenuItemProps = MenuGroup & {
    activeIndex: number | null;
    getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

type ChildMenuItemProps = {
    child: MenuLink;
    activeIndex: number | null;
    getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const PARTIAL_CONFIG_ICON = <FontAwesomeIcon icon={faPlus} className="text-gray-400" />;

const swarmTypes = Object.values(SwarmType).filter(isSwarmType);

function getMenuItemTabIndex(activeIndex: number | null, index: number) {
    if (activeIndex === null) {
        return index === 0 ? 0 : -1;
    }

    return activeIndex === index ? 0 : -1;
}

function ChildMenuItem({ child, activeIndex, getItemProps }: ChildMenuItemProps) {
    const { ref, index } = useListItem({ label: child.label });
    const tabIndex = getMenuItemTabIndex(activeIndex, index);

    return (
        <Link
            ref={ref}
            {...getItemProps({
                tabIndex,
                role: 'menuitem',
            })}
            to={child.to}
            className="flex w-full items-center rounded p-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none"
        >
            {child.icon}
            <span className="ml-3">{child.label}</span>
        </Link>
    );
}

function MenuItem({ label, icon, children: childLinks, activeIndex, getItemProps }: MenuItemProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeChildIndex, setActiveChildIndex] = useState<number | null>(null);
    const listItemsRef = useRef<Array<HTMLElement | null>>([]);
    const listLabelsRef = useRef<Array<string | null>>([]);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'right-start',
        middleware: [
            offset({
                // 12 = 4px padding on this button itself + 8px gap between the next button.
                mainAxis: 12,
                // 5 = 4px padding + 1px border.
                crossAxis: -5,
            }),
            flip(),
            shift({ padding: 8 }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const { ref: listItemRef, index } = useListItem({ label });
    const mergedRef = useMergeRefs([listItemRef, refs.setReference]);

    const click = useClick(context);
    const hover = useHover(context, {
        move: false,
        delay: { open: 100 },
        restMs: 150,
        handleClose: safePolygon({ blockPointerEvents: true }),
    });

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const listNavigation = useListNavigation(context, {
        listRef: listItemsRef,
        activeIndex: activeChildIndex,
        onNavigate: setActiveChildIndex,
        loop: true,
        nested: true,
    });
    const typeahead = useTypeahead(context, {
        listRef: listLabelsRef,
        activeIndex: activeChildIndex,
        onMatch: setActiveChildIndex,
    });
    const {
        getReferenceProps,
        getFloatingProps,
        getItemProps: getChildItemProps,
    } = useInteractions([hover, click, dismiss, role, listNavigation, typeahead]);

    const tabIndex = getMenuItemTabIndex(activeIndex, index);

    return (
        <>
            <button
                ref={mergedRef}
                {...getReferenceProps(
                    getItemProps({
                        tabIndex,
                        role: 'menuitem',
                        'aria-haspopup': 'menu',
                        'aria-expanded': isOpen,
                    }) as React.HTMLProps<HTMLElement>
                )}
                className="flex w-full cursor-pointer items-center justify-between rounded p-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
            >
                <div className="flex items-center">
                    {icon}
                    <span className="ml-3">{label}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="text-gray-400" />
            </button>
            <FloatingPortal>
                {isOpen && (
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className="z-20 w-64 rounded border border-gray-200 bg-white p-1 shadow-lg focus:outline-none"
                    >
                        <FloatingList elementsRef={listItemsRef} labelsRef={listLabelsRef}>
                            {childLinks.map((child) => (
                                <ChildMenuItem
                                    key={child.to}
                                    child={child}
                                    activeIndex={activeChildIndex}
                                    getItemProps={getChildItemProps}
                                />
                            ))}
                        </FloatingList>
                    </div>
                )}
            </FloatingPortal>
        </>
    );
}

export function CreatePartialConfigMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const menuData = useMemo<MenuGroup[]>(
        () =>
            swarmTypes.flatMap((swarmType) => {
                const children = (schema.partialConfigMatrix[swarmType] ?? []).flatMap(
                    (partialType) => {
                        if (!isPartialConfigCategory(partialType)) {
                            return [];
                        }

                        const label = PARTIAL_CONFIG_TYPE_LABELS[partialType];
                        if (!label) {
                            return [];
                        }

                        return [
                            {
                                label,
                                icon: PARTIAL_CONFIG_ICON,
                                to: createPartialConfigCreateUrl(swarmType, partialType, {
                                    absolute: false,
                                }),
                            },
                        ];
                    }
                );

                if (!children.length) {
                    return [];
                }

                return [
                    {
                        label: SwarmTypeTranslations[swarmType],
                        icon: <div className={`${SwarmIconClasses[swarmType]} h-6 w-8`} />,
                        children,
                    },
                ];
            }),
        []
    );

    const listItemsRef = useRef<Array<HTMLElement | null>>([]);
    const listLabelsRef = useRef<Array<string | null>>([]);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'bottom-start',
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const listNavigation = useListNavigation(context, {
        listRef: listItemsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        loop: true,
    });
    const typeahead = useTypeahead(context, {
        listRef: listLabelsRef,
        activeIndex,
        onMatch: setActiveIndex,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        click,
        dismiss,
        role,
        listNavigation,
        typeahead,
    ]);

    return (
        <>
            <Button
                ref={refs.setReference}
                {...getReferenceProps({
                    'aria-haspopup': 'menu',
                    'aria-expanded': isOpen,
                })}
            >
                {gettext('DEFINE_PARTIAL_CONFIGURATION')}
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={classNames('ml-2 transition-transform', {
                        'rotate-180': isOpen,
                    })}
                />
            </Button>
            <FloatingPortal>
                {isOpen && (
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className="z-10 w-64 rounded border border-gray-200 bg-white p-1 shadow-lg focus:outline-none"
                    >
                        <FloatingList elementsRef={listItemsRef} labelsRef={listLabelsRef}>
                            {menuData.map((item) => (
                                <MenuItem
                                    key={item.label}
                                    {...item}
                                    activeIndex={activeIndex}
                                    getItemProps={getItemProps}
                                />
                            ))}
                        </FloatingList>
                    </div>
                )}
            </FloatingPortal>
        </>
    );
}
